

























import { Component, Prop, Ref, Vue, Watch } from "vue-property-decorator";
import Chart from "chart.js";
import { range } from "lodash";

Chart.pluginService.register({
  beforeDraw: function (chart: any) {
    if (!chart.config.options.elements.center) {
      return;
    }

    const dataset = chart.config.data.datasets[0];
    const meta = dataset._meta[Object.keys(dataset._meta)[0]];
    const model = dataset._meta[Object.keys(dataset._meta)[0]].data[0]._model;
    const total = dataset._meta[Object.keys(dataset._meta)[0]].total;

    const percent = String(Math.round((dataset.data[0] / total) * 100));

    const text = chart.config.options.elements.center.text  || percent;

    if (
      !chart.config.options.elements.center.text &&
      !chart.config.options.updatedRadius
    ) {
      chart.config.options.updatedRadius = true;
      model.outerRadius = model.outerRadius + 15;
      model.innerRadius = model.innerRadius + 15;
    }
    // Get ctx from string
    const ctx = chart.chart.ctx;

    // Get options from the center object in options
    const centerConfig = chart.config.options.elements.center;
    const fontStyle = centerConfig.fontStyle || "Arial";
    const txt = centerConfig.text || percent;
    const color = centerConfig.color || "#000";
    const maxFontSize = centerConfig.maxFontSize || 75;
    const sidePadding = centerConfig.sidePadding || 20;
    const sidePaddingCalculated = (sidePadding / 100) * (chart.innerRadius * 2);
    // Start with a base font of 30px
    ctx.font = "30px " + fontStyle;

    // Get the width of the string and also the width of the element minus 10 to give it 5px side padding
    const stringWidth = ctx.measureText(txt).width;
    const elementWidth = chart.innerRadius * 2 - sidePaddingCalculated;

    // Find out how much the font can grow in width.
    const widthRatio = elementWidth / stringWidth;
    const newFontSize = Math.floor(30 * widthRatio);
    const elementHeight = chart.innerRadius * 2;

    // Pick a new font size so it will not be larger than the height of label.
    let fontSizeToUse = Math.min(newFontSize, elementHeight, maxFontSize);
    let minFontSize = centerConfig.minFontSize;
    const lineHeight = centerConfig.lineHeight || 25;
    let wrapText = false;

    if (minFontSize === undefined) {
      minFontSize = 20;
    }

    if (minFontSize && fontSizeToUse < minFontSize) {
      fontSizeToUse = minFontSize;
      wrapText = true;
    }

    // Set font settings to draw it correctly.
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    let centerX = (chart.chartArea.left + chart.chartArea.right) / 2;
    let centerY = (chart.chartArea.top + chart.chartArea.bottom) / 2;
    ctx.font = fontSizeToUse + "px " + fontStyle;
    ctx.fillStyle = color;

    if (!wrapText) {
      ctx.fillText(`${txt}%`, centerX, centerY);
      return
    } 

    const words = txt.split(" ");
    let line = "";
    const lines = [];

    // Break words up into multiple lines if necessary
    for (let n = 0; n < words.length; n++) {
      const testLine = line + words[n] + " ";
      const metrics = ctx.measureText(testLine);
      const testWidth = metrics.width;
      if (testWidth > elementWidth && n > 0) {
        lines.push(line);
        line = words[n] + " ";
      } else {
        line = testLine;
      }
    }

    // Move the center up depending on line height and number of lines
    centerY -= (lines.length / 2) * lineHeight;

    for (var n = 0; n < lines.length; n++) {
      ctx.fillText(lines[n], centerX, centerY);
      centerY += lineHeight;
    }
    //Draw text in center
    ctx.fillText(line, centerX, centerY);
  },
});

export interface DonutData {
  value: number;
  label: string;
}

@Component({
  name: "BiDonut",
})
export default class BiDonut extends Vue {
  @Ref("canvas") canvas!: HTMLCanvasElement;
  @Prop() data!: DonutData[];
  @Prop() title!: string;
  currentSelectedPieceLabel = "";
  chart: Chart | null = null;
  animated = false;

  @Watch("data")
  onData() {
    if (this.data && this.data.length) {
      this.initChart();
    }
  }

  percent(item: DonutData) {
    const value = this.data.reduce((acc, curr) => acc + curr.value, 1);
    return `${Math.round((item.value / value) * 100)}%`;
  }

  get loadingRange() {
    return range(0, 5);
  }

  isSelected(label: string) {
    return label.toLowerCase() === this.currentSelectedPieceLabel.toLowerCase();
  }

  async initChart() {
    await this.$nextTick();
    this.currentSelectedPieceLabel = this.data[0].label;
    this.chart = new Chart(this.canvas, {
      type: "doughnut",
      data: {
        datasets: [
          {
            data: this.data.map(({ value }) => value + ''),
            backgroundColor: [
              "#4E6A7C",
              "#D7E8C7",
              "#D7E8C7",
              "#D7E8C7",
              "#D7E8C7",
            ],
            borderWidth: 5,
          },
        ],
        labels: this.data.map(({ label }) => label),
      },
      options: {
        cutoutPercentage: 65,
        elements: {
          center: {
            color: "#333333", // Default is #000000
            fontStyle: "Gilroy", // Default is Arial
            sidePadding: 20, // Default is 20 (as a percentage)
            minFontSize: 20, // Default is 20 (in px), set to false and text will not wrap.
            lineHeight: 25, // Default is 25 (in px), used for when text wraps
          },
        },
        events: [],
        legend: {
          display: false,
        },
        labelFontFamily: "Arial",
        labelFontStyle: "normal",
        labelFontSize: 24,
        labelFontColor: "#666",
        layout: {
          padding: {
            left: 20,
            right: 20,
            top: 20,
            bottom: 20,
          },
        },
      },
    } as any);
  }

  onClick(event: MouseEvent) {
    if (
      !this.chart ||
      !this.chart.data ||
      !this.chart.data.labels ||
      !this.chart.options.animation
    ) {
      return;
    }
    const addRadiusMargin = 15;
    const activePoints = this.chart.getElementsAtEvent(event) as any[];
    if (!activePoints.length) {
      return (this.canvas.style.cursor = "default");
    }

    this.canvas.style.cursor = "pointer";

    this.chart.options.animation.duration = 0;
    const clickedElementIndex = activePoints[0]["_index"];
    const clickedLabel = this.chart.data.labels[clickedElementIndex] as string;

    if (
      !clickedLabel ||
      clickedLabel.toUpperCase() ===
        this.currentSelectedPieceLabel.toUpperCase()
    ) {
      return;
    }

    const dataset = (this.chart as any).config.data.datasets[0];
    const meta = dataset._meta[Object.keys(dataset._meta)[0]];
    const percent =
      String(
        Math.round((dataset.data[clickedElementIndex] / meta.total) * 100)
      ) ;

    this.currentSelectedPieceLabel = clickedLabel.toUpperCase();

    (this.chart as any).outerRadius = (this.chart as any).outerRadius;
    (this.chart as any).config.options.elements.center.text = percent;

    this.chart.update();

    meta.data.forEach((item: any, i: number) => {
      item._model.backgroundColor =
        i === clickedElementIndex ? "#4E6A7C" : "#D7E8C7";
    });
    activePoints[0]["_model"].outerRadius =
      (this.chart as any).outerRadius + addRadiusMargin;
    activePoints[0]["_model"].innerRadius =
      (this.chart as any).innerRadius + addRadiusMargin;
    this.chart.render({ duration: 0, lazy: false });

  }
}
